import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { analytics } from '@/analytics';
import { type LoginOptions, useCheckLoginEmail, useLogin } from '@/domain/auth';
import type { ApiError } from '@/domain/shared/errors';

import { forgetEmail, readCachedEmail, rememberEmail } from './lastUsedEmail';
import { callbackErrorKey } from './loginCallbackErrors';

export type LoginStep = 'email' | 'provider' | 'password' | 'checkingCache';

export interface LoginValues {
    email: string;
    password: string;
}

export interface LoginFlow {
    step: LoginStep;
    setStep: (step: LoginStep) => void;
    values: LoginValues;
    setValues: (values: LoginValues) => void;
    authOptions: LoginOptions | null;
    checkingEmail: boolean;
    emailError: string | null;
    setEmailError: (error: string | null) => void;
    loading: boolean;
    /** Why the last password sign-in failed. */
    loginError: ApiError | null;
    handleEmailSubmit: (e: FormEvent) => Promise<void>;
    handleProviderLogin: (provider: string) => void;
    handlePasswordLogin: (e: FormEvent) => void;
}

/**
 * Sign-in in steps: the email first, which decides the providers on offer, then either a
 * password or a redirect to the SSO provider. A recent email is remembered and skips ahead.
 */
export function useLoginFlow(): LoginFlow {
    const [step, setStep] = useState<LoginStep>('email');
    const [values, setValues] = useState<LoginValues>({
        email: '',
        password: '',
    });
    const [authOptions, setAuthOptions] = useState<LoginOptions | null>(null);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);

    const { search } = useLocation();
    const query = new URLSearchParams(search);

    const {
        login,
        isLoading: loading,
        error: loginError,
    } = useLogin(() => {
        analytics.loggedIn();
        window.location.replace(query.get('redirectTo') || '/');
    });
    const checkLoginEmail = useCheckLoginEmail();

    const { t } = useTranslation();

    // Handle error messages from the callback
    useEffect(() => {
        const error = query.get('error');
        if (error) {
            setEmailError(t(callbackErrorKey(error)));
        }
    }, [query, t]);

    const verifyEmailAndAdvance = useCallback(
        async (email: string) => {
            if (!email) return;

            setCheckingEmail(true);
            setEmailError(null);

            try {
                // Check the email domain and get the available providers
                const check = await checkLoginEmail(email);

                if (check.outcome !== 'ok') {
                    if (check.outcome === 'invalid') {
                        setEmailError(check.message || t('publicPages.login.errors.invalidEmail'));
                    } else if (check.outcome === 'unknownDomain') {
                        setEmailError(t('publicPages.login.errors.domainNotFound'));
                    } else {
                        setEmailError(t('publicPages.login.errors.domainCheckFailed'));
                    }
                    forgetEmail();
                    setStep('email'); // Ensure we return to email entry on failure
                    return;
                }

                const data = check.options;
                setAuthOptions(data);
                rememberEmail(email);

                // If local auth is the only or default option, go to password step
                const localAuth = data.authProviders.find((p) => p.name === 'local');
                if (localAuth && (data.authProviders.length === 1 || localAuth.isDefault)) {
                    setStep('password');
                    return;
                }

                // Otherwise, show provider selection
                setStep('provider');
            } catch (error) {
                console.error('Error checking email:', error);
                setEmailError(t('publicPages.login.errors.unexpected'));
                forgetEmail();
                setStep('email');
            } finally {
                setCheckingEmail(false);
            }
        },
        [t, checkLoginEmail]
    );

    // Check for cached email on component mount and auto-advance
    useEffect(() => {
        const cached = readCachedEmail();
        if (cached.status === 'valid') {
            setStep('checkingCache');
            setValues((prev) => ({ ...prev, email: cached.email }));
            verifyEmailAndAdvance(cached.email);
        } else if (cached.status === 'expired') {
            forgetEmail();
            setStep('email'); // Stay on the 'email' step
        }
    }, [verifyEmailAndAdvance]);

    const handleEmailSubmit = async (e: FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        await verifyEmailAndAdvance(values.email);
    };

    const handleProviderLogin = (provider: string) => {
        if (provider === 'local') {
            setStep('password');
            return;
        }

        // Redirect to the appropriate SSO provider
        window.location.href = `/api/v2/auth/${provider}/login?email=${encodeURIComponent(
            values.email
        )}&redirectTo=${encodeURIComponent(query.get('redirectTo') || '/')}`;
    };

    const handlePasswordLogin = (e: FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        login({
            username: values.email,
            password: values.password,
        });
    };

    return {
        step,
        setStep,
        values,
        setValues,
        authOptions,
        checkingEmail,
        emailError,
        setEmailError,
        loading,
        loginError,
        handleEmailSubmit,
        handleProviderLogin,
        handlePasswordLogin,
    };
}
